<template>
<div>
  <q-drawer
      show-if-above
      class="sidebar sidebar-left project-index__sidebar"
      side="left"
      bordered
      :mini="!leftDrawerOpen || miniState"
      :mini-width="$isDesktop ? 64 : 36"
      :width="drawerWidth"
      :breakpoint="0"
  >
    <div class="column full-height">
      <div class="col-shrink sidebar__header">
        <div class="row items-start"
             :class="{'justify-between q-ma-md': !miniState, 'justify-center q-my-md': miniState}">
          <common-input
              v-model="search"
              @update:model-value="searchChanged"
              @blur="reloadProjects"
              clearable
              class="form__input q-mini-drawer-hide"
              label="Zoekterm"
              dense
          />
          <icon-button
              :id="miniState ? 'sidebar-close' : 'sidebar-open'"
              :size="$isDesktop ? 'md' : 'sm'"
              :class="{'rotate-180': !miniState}"
              :icon="$isDesktop ? 'double_arrow' : miniState ? 'list' : 'map'"
              :aria-label="$isDesktop
              ? (miniState ? 'Open linker sidebar' : 'Sluit linker sidebar')
              : (miniState ? 'Toon lijst' : 'Toon kaart')"
              @click="toggleDrawer"
          />
        </div>

        <div class="row q-mx-md q-mb-xs q-mini-drawer-hide">
          <q-btn-dropdown flat dense class="col-5"
                          @hide="reloadProjects">
            <template v-slot:label>
              <span class="text-weight-medium q-mr-sm ellipsis">Fases</span>
              <count-chip v-if="phaseFilter.length" :count="phaseFilter.length" />
            </template>
            <q-list style="min-width: 100px">
              <q-item v-for="phase in phases"
                      clickable
                      :key="phase.id">
                <q-checkbox v-model="phaseFilter"
                            :val="phase.id"
                            :label="phase.title"
                            @update:model-value="filtersUpdated = true" />
              </q-item>
            </q-list>
          </q-btn-dropdown>
          <q-btn-dropdown flat dense
                          class="col-6"
                          @hide="reloadProjects">
            <template v-slot:label>
              <span class="text-weight-medium q-mr-sm ellipsis">Categorie&euml;n</span>
              <count-chip v-if="categoryFilter.length" :count="categoryFilter.length" />
            </template>

            <q-list style="min-width: 100px">
              <q-item v-for="category in categories"
                      clickable
                      :key="category.id">
                <q-checkbox v-model="categoryFilter"
                            :val="category.id"
                            :label="category.title"
                            @update:model-value="filtersUpdated = true" />
              </q-item>
            </q-list>
          </q-btn-dropdown>
          <div class="col-1">
            <icon-button
                class="menu-button"
                :class="{'menu-button--alerted': hasHiddenFilters}"
                icon="more_vert"
                text-color="neutral-90"
                size="sm"
                :aria-label="`Toon menu${ hasHiddenFilters ? ' (verborgen filters)': ''}`"
                round>
              <actionable-menu separator
                               :autoClose="false">
                <q-item v-if="themes.length">
                  <q-btn-dropdown flat dense
                                  class="full-width">
                    <template v-slot:label>
                      <span class="text-weight-medium q-mr-sm ">Thema's</span>
                      <count-chip v-if="themeFilter.length" :count="themeFilter.length" />
                    </template>
                    <q-list style="min-width: 100px">
                      <q-item v-for="theme in themes"
                              clickable
                              :key="theme.id">
                        <q-checkbox v-model="themeFilter"
                                    :val="theme.id"
                                    :label="theme.title"
                                    @update:model-value="searchChanged" />
                      </q-item>
                    </q-list>
                  </q-btn-dropdown>
                </q-item>
                <q-item v-if="$can('manage-projects')">
                  <q-toggle
                      v-model="searchActionable"
                      label="Wachtend op toetsing"
                      @update:model-value="reloadProjects(true)"
                  />
                </q-item>
                <q-item v-if="$can('manage-projects')">
                  <q-toggle
                      v-model="searchMyProjects"
                      label="Mijn projecten"
                      @update:model-value="reloadProjects(true)"
                  />
                </q-item>
                <q-item>
                  <q-toggle
                      v-model="searchArchive"
                      label="Projecten in het archief"
                      @update:model-value="reloadProjects(true)"
                  />
                </q-item>
              </actionable-menu>
            </icon-button>
          </div>
        </div>
      </div>
      <div class="col q-mini-drawer-hide" ref="projectList">
        <scroll-area class="fit">
          <q-list>
            <q-item v-for="project in projects"
                    clickable
                    :key="project.properties.uuid"
                    :class="{item__active: currentHighlight === project.properties.uuid}"
                    @click="openProject(project.properties)"
                    @mouseenter="highlightGeometry(project.properties)"
            >
              <q-item-section avatar>
                <status-dot
                    :status="project.phase_status.waiting_on_approval || project.phase_status.waiting_on_reapproval ?
                     'pending': 'uncompleted'" />

              </q-item-section>
              <q-item-section>
                <q-item-label>{{ project.properties.title }}</q-item-label>
                <q-item-label caption>Laatst gewijzigd: {{
                    $formatDateRelative(project.properties.updated_at)
                  }}
                </q-item-label>
              </q-item-section>
            </q-item>
            <q-item class="justify-center my-2">
              <div>
                <LoadingIndicator v-if="isLoading" />
                <q-item v-else-if="pagination?.last_page > 1" class="flex flex-center">
                  <pagination-links :items="pagination" @navigate="loadPage" />
                </q-item>
              </div>
            </q-item>
          </q-list>
        </scroll-area>
      </div>
    </div>
  </q-drawer>

  <q-page class="main project-index__map">
    <BaseMap width="100%"
             height="100%"
             ref="map"
             :canFullscreen="false"
             :isLoading="isLoading"
             :geometry="projectGeometry"
             @feature:click="openProject"
             @feature:hover="highlightProject" />

    <sticky-bottom-bar>
      <q-btn
          v-if="$can('import-projects-from-external')"
          round
          color="primary"
          icon="download"
          aria-label="Importeer project(en)"
          size="md"
          class="q-mr-md"
          @click="$goto('project-import.index')">
        <q-tooltip class="bg-neutral-80 text-white"
                   anchor="center start"
                   self="center right">
          Importeer project(en)
        </q-tooltip>
      </q-btn>
      <common-fab v-if="$can('create-projects')" label="Nieuw project" @click="$goto('search.start')" />
    </sticky-bottom-bar>
  </q-page>
</div>
</template>

<script>
import StatusDot from '~HIOR/components/element/StatusDot'
import { momentMixin } from '~HIOR/mixins'
import { dom } from 'quasar'
import FabAction from '../../components/button/FabAction.vue'

export default {
  mixins: [momentMixin],
  components: { FabAction, StatusDot },
  props: {
    phases: {
      type: Array,
      required: true,
    },
    categories: {
      type: Array,
      required: true,
    },
    themes: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    page: 1,
    leftDrawerOpen: true,
    drawerOpen: false,
    miniState: false,
    isLoading: false,
    filtersUpdated: false,
    search: null,
    currentHighlight: null,
    projectGeometry: null,
    projects: [],
    phaseFilter: [],
    pagination: null,
    ordering: {
      column: 'projects.updated_at',
      direction: 'DESC',
    },
    categoryFilter: [],
    themeFilter: [],
    searchActionable: false,
    searchMyProjects: false,
    searchArchive: false,
  }),
  computed: {
    drawerWidth () {
      if (this.$isDesktop) return 350
      return dom.width(document.body)
    },
    hasHiddenFilters () {
      return this.searchArchive ||
          this.searchMyProjects ||
          this.searchActionable ||
          this.themeFilter.length > 0
    },
  },
  methods: {
    searchChanged () {
      this.filtersUpdated = true
      this.reloadProjectsDebounced()
    },
    reloadProjectsDebounced: _.debounce(function () {
      this.reloadProjects()
    }, 500),
    reloadProjects (force = false) {
      if (!this.filtersUpdated && !force) {
        return
      }
      this.filtersUpdated = false
      this.loadPage(1)
    },
    loadPage (page) {
      this.page = page
      this.projects = []

      const url = new URL(window.location)
      url.searchParams.set('page', this.page)
      window.history.pushState(null, '', url.toString())
      this.loadProjects()
    },
    async loadProjects () {
      this.isLoading = true
      try {
        const params = {
          per_page: 25,
          srid: 4326,
          page: this.page,
          order_by: this.ordering.column,
          order_direction: this.ordering.direction,
        }

        if (this.categoryFilter.length) {
          params.categories = this.categoryFilter
        }
        if (this.phaseFilter.length) {
          params.phases = this.phaseFilter
        }
        if (this.themeFilter.length) {
          params.themes = this.themeFilter
        }
        if (this.search) {
          params.search = this.search
        }
        if (this.searchArchive) {
          params.in_archive = true
        }
        if (this.searchMyProjects) {
          params.for_user = true
        }
        if (this.searchActionable) {
          params.is_actionable = true
        }
        const { data } = await axios.get(this.$route('api.projects.internal', params))
        this.projects = data.features
        this.projectGeometry = data
        this.pagination = data.meta
      } catch (e) {
        console.error(e)
      }
      await this.$nextTick()
      this.isLoading = false
    },
    async highlightProject ({ uuid }) {
      this.$refs.map.highlightFeature(uuid)
      this.currentHighlight = uuid
      await this.$nextTick()
      const listItem = this.$refs.projectList.getElementsByClassName('item__active')
      if (listItem.length === 0) return
      listItem[0].scrollIntoView({ behavior: 'smooth' })
    },
    highlightGeometry ({ uuid }) {
      this.$refs.map.highlightFeature(uuid, true)
      this.currentHighlight = uuid
    },
    removeHighlights () {
      this.$refs.map.removeHighlights()
      this.currentHighlight = null
    },
    openProject (project) {
      this.$goto('project.show', project.uuid)
    },
    toggleDrawer () {
      this.miniState = !this.miniState
      this.$refs.map?.redraw()
    },
  },
  mounted () {
    const searchParams = new URLSearchParams(window.location.search)
    this.page = searchParams.get('page') || 1
    this.loadProjects()
  },
}
</script>

<style lang="scss">
.project-index {
  &__sidebar {
    .item__active {
      .q-item__section {
        font-weight: bold;
      }
    }
  }

  &__map {
    padding: 0;
    position: relative;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;

    .map {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      height: 100%;
    }

    .q-page-sticky {
      margin-bottom: 60px !important;
      z-index: 1000;
    }
  }
}
</style>
